// ============================================================
// LEVELS - the storeys of one column, and which of them are yours.
//
// The map has known since v7.43.0 that a floating slab is a ceiling to
// somebody underneath it and a floor to somebody on it. Nothing ever
// SAID so: a marker under a gantry and a marker on it looked identical,
// and the only way between them was a wheel that gave no sign of what it
// was passing through. "Sometimes they stand under the platform,
// sometimes on top of it, and I cannot get them back up."
//
// This is that question asked out loud. What can be stood on here, what
// is each of them called, and which may THIS combatant get to from where
// they are.
//
// It answers "may I", never "what did it cost" - the movement rules own
// that, and charge for the rise through `climbCost` (v7.95.0).
//
// Pure. Zero React dependency.
// ============================================================
import { surfaceCandidatesAt } from './reconcile';
import type { Surface } from './reconcile';
import type { Solid } from './types';

/**
 * How close to a surface still counts as standing on it, in metres.
 *
 * A centimetre. A drag settles a token by accumulating floats and lands
 * a hair off the exact top; matching on equality would then say the
 * marker is nowhere and light no level at all.
 */
export const LEVEL_EPSILON = 0.01;

/** A storey of one column. */
export interface Level {
  /** The height stood on. */
  top: number;
  /** Where the slab starts - its underside, and a ceiling to anyone below. */
  base: number;
  /** What to call it: the author's name, or the ground. */
  label: string;
  /** The solid it is the top of. Absent on the ground itself. */
  solidId?: string;
  /** Whether this combatant is on it now. */
  here: boolean;
  /** Whether they could get to it from where they are. */
  reachable: boolean;
}

export interface LevelOptions {
  /** Where the ground itself is; negative inside a trench. */
  floor?: number;
  /** The height their feet are at now. Omitted: every level is offered. */
  from?: number;
  /** How far up still counts as a stride rather than a climb. */
  maxStep?: number;
  /** Whether they can climb, which reaches the next floor up. */
  climbs?: boolean;
  /** Whether they choose their own altitude, in both directions. */
  flies?: boolean;
  /**
   * Whether they may step DOWN to a lower storey.
   *
   * The picker was climb-only on the ruling that you come down by walking
   * off the edge. Walking off the edge did not work - the going was read
   * at ankle height, so a roof reported impassable and froze whoever was
   * on it - which left the list a one-way trip. Both are fixed; this is
   * the half that is a choice rather than a bug (v7.97.0).
   */
  descends?: boolean;
}

/** The ground has no author to have named it. */
const GROUND = 'Ground';

/** Something is there and nobody named it. */
const UNNAMED = 'Structure';

/**
 * Every storey of the column at this point, lowest first.
 *
 * WHICH OF THEM ARE YOURS depends on where you stand: a stride up, a
 * base already underfoot carrying you, one storey of climb, and - with
 * `descends` - a storey down. Flyers get all of them, because choosing
 * an altitude is the whole of what flying is.
 *
 * Walking off the edge is still a way down and always was the intended
 * one; it simply could not be relied on while a roof reported impassable
 * and froze whoever stood on it (v7.97.0).
 */
export function levelsAt(x: number, z: number, solids: Solid[], options: LevelOptions = {}): Level[] {
  const { from } = options;
  const surfaces = dedupe(surfaceCandidatesAt(x, z, solids, options.floor ?? 0));
  const levels = surfaces
    .filter((surface) => hasRoom(surfaces, surface.top) || isHere(surface.top, from))
    .map((surface) => toLevel(surface, from));
  if (from !== undefined) markReachable(levels, from, options);
  return levels;
}

/**
 * A SURFACE WITH SOLID MATTER ON IT IS NOT A STOREY.
 *
 * `descends` made this visible: the column under a five-metre block of
 * glass has a ground at 0 and a roof at 5, and stepping "down" put the
 * marker inside the glass. The ground beneath a floating GANTRY is a real
 * floor - there is room there - and the ground beneath a solid block is
 * not, and the two look identical until you ask what is standing on it.
 *
 * Straddling: its base is at or below this height and its top is above
 * it. A block sitting flush ON the ground has base exactly 0, so the
 * tolerance has to fall on the generous side of both comparisons -
 * `base <= top - EPSILON` would let the commonest case straight through,
 * which is the one this exists for.
 */
function hasRoom(surfaces: Surface[], top: number): boolean {
  return !surfaces.some((s) => s.base <= top + LEVEL_EPSILON && s.top > top + LEVEL_EPSILON);
}

/**
 * Where they are NOW is kept regardless. Terrain gets built over people
 * and markers get placed with terrain switched off; you can always leave
 * where you are, which is the rule the movement fill's own escape clause
 * follows.
 */
function isHere(top: number, from: number | undefined): boolean {
  return from !== undefined && Math.abs(top - from) <= LEVEL_EPSILON;
}

function toLevel(surface: Surface, from: number | undefined): Level {
  return {
    top: surface.top,
    base: surface.base,
    label: surface.solidId ? surface.label || UNNAMED : GROUND,
    ...(surface.solidId ? { solidId: surface.solidId } : {}),
    here: isHere(surface.top, from),
    reachable: true,
  };
}

/**
 * Which levels a step from `from` can reach.
 *
 * A CLIMB IS ONE STOREY, not a lift to the roof. Granting every level
 * above would let a marker be posted to the top of a tower in a single
 * gesture, and the movement rules would never see the metres.
 */
function markReachable(levels: Level[], from: number, { maxStep, climbs, flies, descends }: LevelOptions): void {
  for (const level of levels) level.reachable = canReach(level, from, { maxStep, flies, descends });
  if (!climbs || flies) return;
  const next = levels.find((level) => level.top > from + LEVEL_EPSILON);
  if (next) next.reachable = true;
}

/** Two slabs meeting flush are one floor, and must offer one choice. */
function dedupe(surfaces: ReturnType<typeof surfaceCandidatesAt>) {
  return surfaces.filter((s, i) => i === 0 || s.top - surfaces[i - 1].top > LEVEL_EPSILON);
}

function canReach(level: { top: number; base: number; here: boolean }, from: number, o: LevelOptions): boolean {
  if (level.here) return true;
  if (o.flies) return true;
  // DOWN IS A DROP, and one storey of it at a time - the arrows step, so
  // a tower is descended a floor per gesture just as it is climbed.
  if (level.top < from - LEVEL_EPSILON) return !!o.descends;
  // The two ways onto something, unchanged from `reachableSurface`: a
  // stride up onto it, or its base already underfoot and carrying you.
  if (o.maxStep !== undefined && level.top <= from + o.maxStep + LEVEL_EPSILON) return true;
  return level.base <= from + LEVEL_EPSILON;
}

/**
 * The level an arrow moves to, or null when that arrow does nothing.
 *
 * It steps to the next REACHABLE one rather than the next one, so a
 * ceiling in the way never dead-ends a flyer who could pass it - and a
 * disabled arrow means there is genuinely nowhere that way.
 */
export function levelStep(levels: Level[], direction: 1 | -1): Level | null {
  const at = levels.findIndex((level) => level.here);
  if (at < 0) return null;
  for (let i = at + direction; i >= 0 && i < levels.length; i += direction) {
    if (levels[i].reachable) return levels[i];
  }
  return null;
}
